import { useCallback, useState } from 'react';
import membersApi from '../api/membersApi';

const matchesGroup = (member, groupId) => {
  if (groupId === null || groupId === undefined) return true;
  return Number(member && member.groupId) === Number(groupId);
};

const resolveOperationCustomerId = (scope, fixedCustomerId, memberLike) => {
  if (scope === 'support') return Number(fixedCustomerId);
  return Number(memberLike && memberLike.groupId);
};

const buildListRequest = async ({ scope, fixedCustomerId, filters, appProviderId, groupFilter }) => {
  if (scope === 'support') {
    const pagedResult = await membersApi.getSupportMembers(fixedCustomerId, {
      ...filters,
      appProviderId,
    });

    return {
      items: (pagedResult.items || []).filter((member) => matchesGroup(member, groupFilter)),
      totalCount: Number(pagedResult.totalCount || 0),
      page: Number(pagedResult.page || 0),
      pageSize: Number(pagedResult.pageSize || filters.pageSize || 0),
      totalPages: Number(pagedResult.totalPages || 0),
    };
  }

  const pagedResult = await membersApi.getInternalMembers({
    ...filters,
    customerId: filters.customerId,
    appProviderId,
  });

  return {
    items: pagedResult.items || [],
    totalCount: Number(pagedResult.totalCount || 0),
    page: Number(pagedResult.page || 0),
    pageSize: Number(pagedResult.pageSize || filters.pageSize || 0),
    totalPages: Number(pagedResult.totalPages || 0),
  };
};

export default function useMembers({ scope, customerId = null, appProviderId = null, groupFilter = null, lockedGroupId = null }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ totalCount: 0, page: 0, pageSize: 0, totalPages: 0 });

  let appId = appProviderId;

  if(appId === null) {
    appId = localStorage.getItem('appid');
  }


  const loadMembers = useCallback(async (filters = {}) => {
    setLoading(true);
    setError('');

    try {
      const result = await buildListRequest({
        scope,
        fixedCustomerId: customerId,
        filters,
        appProviderId: appId,
        groupFilter,
      });
      setMembers(result.items || []);
      setPagination({
        totalCount: Number(result.totalCount || 0),
        page: Number(result.page || 0),
        pageSize: Number(result.pageSize || 0),
        totalPages: Number(result.totalPages || 0),
      });
      return result;
    } catch (err) {
      const detail = err && err.response && err.response.data ? err.response.data : (err && err.message) || 'Failed to load members.';
      setError(String(detail));
      setMembers([]);
      setPagination({ totalCount: 0, page: 0, pageSize: 0, totalPages: 0 });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [scope, customerId, appProviderId, groupFilter]);

  const saveMember = useCallback(async (member) => {
    setLoading(true);
    setError('');

    const operationCustomerId = resolveOperationCustomerId(scope, customerId, member);
    const effectiveLockedGroupId = scope === 'support' ? lockedGroupId : null;

    try {
      if (member && member.id) {
        return await membersApi.updateSupportMember(
          operationCustomerId,
          member.id,
          member,
          effectiveLockedGroupId,
          { appProviderId, supportRestrictedUpdate: scope === 'support' }
        );
      }

      return await membersApi.createSupportMember(operationCustomerId, member, effectiveLockedGroupId, { appProviderId });
    } catch (err) {
      const detail = err && err.response && err.response.data ? err.response.data : (err && err.message) || 'Failed to save member.';
      setError(String(detail));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [scope, customerId, lockedGroupId, appProviderId]);

  const deleteMember = useCallback(async (member) => {
    setLoading(true);
    setError('');

    try {
      const operationCustomerId = resolveOperationCustomerId(scope, customerId, member);
      return await membersApi.deleteSupportMember(operationCustomerId, member.id, { appProviderId });
    } catch (err) {
      const detail = err && err.response && err.response.data ? err.response.data : (err && err.message) || 'Failed to delete member.';
      setError(String(detail));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [scope, customerId, appProviderId]);

  return {
    members,
    loading,
    error,
    pagination,
    loadMembers,
    saveMember,
    deleteMember,
    setMembers,
  };
}
